// - - - - - - - - - -
// Pure capacity-remaining helper for issue #140 — "THE BIG ONE: Partial
// Capacity Availability & Recommended Fillers."
//
// Computes how much carpet capacity an installer has remaining on a specific
// date, based on their Candidate-row `capacity_carpet_once` / `_twice` fields
// and the carpet ActiveQueue docs currently assigned to them on that date.
//
// Scope decisions (resolved 2026-05-19 with Brandon via Leland):
//   • CARPET ONLY for v1. Other surfaces are deferred follow-ups.
//   • ANY installer with non-zero carpet capacity is eligible — eligibility
//     is `capacity_carpet_once > 0 OR capacity_carpet_twice > 0`. NOT gated
//     on `Scheduling_Surface === "Soft Surface"` (relaxed 2026-05-20 per
//     issue #147 — hard-surface installers can legitimately carry carpet
//     capacity values; Brandon expects them to surface for partial-capacity
//     when their carpet day isn't fully booked).
//   • UNIT CONVERSION (2026-05-19 UAT review #3): the `capacity_carpet_once`
//     and `capacity_carpet_twice` fields are STORED AS SQUARE FEET in the
//     Candidates collection — despite the lack of a unit suffix in the field
//     name. Carpet jobs render and compare in YARDS (1 sq yd = 9 sqft) per
//     the existing `schedule.context.js:1018-1020` convention. This helper
//     divides the raw capacity values by SQFT_PER_SQ_YD = 9 BEFORE doing any
//     comparison, so the rest of this module operates in yards. Both the
//     math AND the popover header display end up in yards consistently.
//     furniture and steps capacity fields (`capacity_carpet_*_furniture` /
//     `_steps`) are piece counts (dimensionless) and are NOT converted.
//   • STOPS HARD CAPS (2026-05-20, issue #146): two new per-installer fields
//     on Candidates — `capacity_stops_jobs` and `capacity_stops_repairs` —
//     act as HARD CAPS on the recommender. 1 job entry consumes 1 job-stop;
//     1 repair entry consumes 1 repair-stop. The day is "fully booked on
//     stops" when either remaining = 0, regardless of yards/furniture/steps
//     headroom. Jobs vs Repairs are tracked separately (Brandon's spec).
//     "Not configured" semantic: when a field is missing/null/NaN, the cap
//     is treated as NOT enforced (`Infinity` remaining). This is the safe
//     transition default while FileMaker is still rolling out the new fields
//     to all installers. Once an installer's caps are populated (including
//     explicit 0), the gate kicks in. `isStopsJobsCapConfigured` and
//     `isStopsRepairsCapConfigured` flags expose this state to callers.
//   • _once vs _twice is inferred from the count of CARPET jobs (not
//     any-surface count) already assigned to the installer on that date:
//       - 0 or 1 carpet jobs → use `capacity_carpet_once`
//       - 2+ carpet jobs    → use `capacity_carpet_twice`
//     A mixed-surface day (e.g. 1 carpet + 1 vinyl) stays in _once for carpet
//     because `capacity_carpet_twice` semantically means "second carpet load
//     that day."
//   • Per-surface independence: carpet remaining is computed solely from
//     carpet assignments and the carpet capacity fields. Vinyl/plank/etc.
//     assignments on the same day do not consume carpet budget.
//   • `capacity_carpet_twice = 0` indicates the installer isn't qualified for
//     double-load carpet days. If they somehow accumulate 2+ carpet jobs, the
//     effective cap stays at 0 → remaining clamps to 0 → no partial-capacity
//     flag fires. The recommender will return empty, which is correct.
//   • Multi-day carpet jobs (date_scheduled → date_estimatedCompletion) count
//     toward EACH day in their range, mirroring the production render logic
//     used in #138 (`shouldHighlightEmptyTomorrowCell`) and `schedule.context.js:1893`.
//     The full yardage is debited from EACH covered day's budget because the
//     installer is occupied with that job each day.
//
// This helper is consumed by:
//   • `shouldHighlightPartialCapacityCell` — the cell-level boolean decision
//   • `recommendFillerCarpetJobs` — uses the `remaining` value to filter
//   • Future #139 dashboard partial-capacity category (v1.1 follow-up)
//
// Test coverage: see `installerCapacityRemaining.test.js`. Adversarial cases
// covered include the _twice=0 clamp, threshold flip from _once to _twice as
// count crosses 2, mixed-surface days, multi-day jobs, NaN/empty yardage,
// and the missing-Materials edge case.

// - - - - - - - - - -
// Unit conversion: capacity_carpet_once / _twice are STORED AS SQFT in
// Candidates (2026-05-19 UAT review #3). Carpet jobs are measured in yards.
// Divide capacity by 9 to compare yards-to-yards.
const SQFT_PER_SQ_YD = 9;

// - - - - - - - - - -
// Display helper — rounds yardage values to 1 decimal max for display and
// trims trailing zero so clean integers render as "150" not "150.0".
// Exported so all #140 display sites use a single source of truth (popover
// rows, popover header capacity summary, icon title attribute).
//
// Examples:
//   formatYds(150)              → "150"
//   formatYds(150.5)            → "150.5"
//   formatYds(25.3333333333333) → "25.3"   // captures the user's 2026-05-19
//                                          // dev-server feedback: "your yards
//                                          // calculation needs to be limited
//                                          // to one decimal point accuracy"
//   formatYds(0)                → "0"
//   formatYds(undefined)        → "0"
//   formatYds(null)             → "0"
//   formatYds("36")             → "36"     // string inputs from FileMaker
//                                          // serialization coerced
//   formatYds(NaN)              → "0"      // poison-input safety
export const formatYds = (n) => {
  const num = parseFloat(n);
  if (isNaN(num)) return "0";
  // toFixed(1) → 1-decimal string; Number() → strips the trailing ".0" for
  // clean integers; toString() → canonical string output.
  return Number(num.toFixed(1)).toString();
};

// - - - - - - - - - -
// Local helpers — kept private to the module since they are highly specific
// to this calc and would muddy the public API surface.

// Mirrors the inline `new Date(cell) >= new Date(start) && new Date(cell) <=
// new Date(end)` pattern in schedule.context.js (~30 sites) and in
// highlightEmptyTomorrowCell.js. Empty `end` is treated as a single-day job.
// Exported 2026-05-21 (Brandon UAT bug #153) so the cell-trigger's SHARED-
// suppression gate can use the SAME multi-day awareness as the capacity
// counter — keeping the two in lockstep when a multi-day job spans across
// SHARED-with-other-location days.
export const dateInJobRange = (cellDate, jobStart, jobEnd) => {
  if (!cellDate || !jobStart) return false;
  const cellMs = new Date(cellDate).getTime();
  const startMs = new Date(jobStart).getTime();
  if (isNaN(cellMs) || isNaN(startMs)) return false;
  const effectiveEnd = jobEnd || jobStart;
  const endMs = new Date(effectiveEnd).getTime();
  if (isNaN(endMs)) return false;
  return cellMs >= startMs && cellMs <= endMs;
};

// Sums Materials[].total_yds for the Carpet rows inside a single ActiveQueue
// doc. Returns 0 if Materials is missing/empty or contains no Carpet row.
// Pad rows are explicitly ignored (matches `schedule.context.js:1049-1050`).
//
// Defensive against:
//   • Missing Materials array
//   • Material entries with no FileMaker-prefixed keys
//   • total_yds rendered as a string ("36" vs 36) — parseFloat coerces both
//   • NaN / negative values — clamp to 0 so a single bad doc cannot poison
//     the daily total or produce a negative remaining
export const getCarpetYardsForJob = (doc) => {
  if (!doc || !Array.isArray(doc.Materials)) return 0;
  let total = 0;
  for (const material of doc.Materials) {
    if (!material) continue;
    const type = material["KI_AQ » Materials::type"];
    if (type !== "Carpet") continue;
    const yds = parseFloat(material["KI_AQ » Materials::total_yds"]);
    if (!isNaN(yds) && yds > 0) total += yds;
  }
  return total;
};

// - - - - - - - - - -
// Per-job furniture and steps piece counts (2026-05-19 UAT review #2).
//
// Mechanism: scan `InvoiceLineItems[]`. For each line item whose SKU is in
// the matching list (JOB_FURNITURE_SKULIST / JOB_STEPS_SKULIST), accumulate
// its `KI_AQ » InvoiceLineItems::qty`. The summed qty is the count of
// pieces the installer must handle for that job.
//
// Why qty and not just `count of matched lines`: a single line item can
// represent multiple pieces (e.g. qty=3 → 3 furniture units in one line).
// The installer's capacity_*_furniture / _steps fields are units-per-day,
// so matching at the qty level is the correct dimension.
//
// Defensive against the same edge cases as getCarpetYardsForJob: missing
// InvoiceLineItems, missing keys, string qtys, NaN, negative.
//
// `parseInt(sku)` matches the existing pattern in schedule.context.js:1076
// for the furniture-flag boolean calc — SKUs arrive as strings or numbers
// from FileMaker; parseInt normalizes both forms.

const sumLineItemQtyForSkuList = (doc, skuList) => {
  if (!doc || !Array.isArray(doc.InvoiceLineItems)) return 0;
  if (!Array.isArray(skuList) || skuList.length === 0) return 0;
  let total = 0;
  for (const item of doc.InvoiceLineItems) {
    if (!item) continue;
    const sku = parseInt(item["KI_AQ » InvoiceLineItems::sku"]);
    if (isNaN(sku)) continue;
    if (!skuList.includes(sku)) continue;
    const qty = parseFloat(item["KI_AQ » InvoiceLineItems::qty"]);
    if (!isNaN(qty) && qty > 0) total += qty;
  }
  return total;
};

export const getCarpetFurniturePiecesForJob = (doc, furnitureSkuList) =>
  sumLineItemQtyForSkuList(doc, furnitureSkuList);

export const getCarpetStepsForJob = (doc, stepsSkuList) =>
  sumLineItemQtyForSkuList(doc, stepsSkuList);

// - - - - - - - - - -
// Public helper.

/**
 * Computes carpet capacity remaining for one installer on one date.
 *
 * @param {Object} params
 * @param {Object} params.candidate           — installer Candidate doc. Must
 *                                              include `payrollNo`,
 *                                              `Scheduling_Surface`,
 *                                              `capacity_carpet_once`,
 *                                              `capacity_carpet_twice`,
 *                                              `capacity_carpet_once_furniture`,
 *                                              `capacity_carpet_twice_furniture`,
 *                                              `capacity_carpet_once_steps`,
 *                                              `capacity_carpet_twice_steps`.
 * @param {string} params.dateString          — the date to evaluate, formatted
 *                                              "M/D/YYYY" to match
 *                                              `weekDatesArray` entries.
 * @param {Array}  params.activeQueueDocs     — raw ActiveQueue docs (NOT
 *                                              projected scheduleArray tiles)
 *                                              with Materials + InvoiceLineItems
 *                                              still intact. Typically
 *                                              `filteredProjectsList` from
 *                                              ScheduleContext.
 * @param {Array}  params.furnitureSkuList    — SKUs matching furniture line
 *                                              items. Pass `JOB_FURNITURE_SKULIST`
 *                                              from keys.js.
 * @param {Array}  params.stepsSkuList        — SKUs matching steps line items.
 *                                              Pass `JOB_STEPS_SKULIST` from
 *                                              keys.js. Empty list is fine
 *                                              (treated as inert constraint
 *                                              until Brandon populates it).
 * @param {Function} [params.normalizePayroll] — optional payroll formatter
 *                                              (e.g. `dataFormatting_payrollFields`).
 *                                              Used to compare candidate.payrollNo
 *                                              against doc.installer when one
 *                                              side is numeric and the other
 *                                              is a "N.NN" string. Defaults to
 *                                              identity.
 *
 * @returns {Object} {
 *   capacityOnce,                — `capacity_carpet_once` numeric (yards)
 *   capacityTwice,               — `capacity_carpet_twice` numeric (yards)
 *   effectiveCap,                — yards cap (once or twice based on count)
 *   capacityOnceFurniture,       — `capacity_carpet_once_furniture` numeric
 *   capacityTwiceFurniture,      — `capacity_carpet_twice_furniture` numeric
 *   effectiveFurnitureCap,       — furniture cap (once or twice based on count)
 *   capacityOnceSteps,           — `capacity_carpet_once_steps` numeric
 *   capacityTwiceSteps,          — `capacity_carpet_twice_steps` numeric
 *   effectiveStepsCap,           — steps cap (once or twice based on count)
 *   capacityStopsJobs,           — `capacity_stops_jobs` numeric (or 0 if
 *                                  not configured) — #146
 *   capacityStopsRepairs,        — `capacity_stops_repairs` numeric (or 0
 *                                  if not configured) — #146
 *   effectiveStopsJobsCap,       — stops-jobs cap after one-load clamp — #146
 *   effectiveStopsRepairsCap,    — stops-repairs cap after one-load clamp — #146
 *   isStopsJobsCapConfigured,    — true iff capacity_stops_jobs is a real
 *                                  number on the doc (vs missing/null) — #146
 *   isStopsRepairsCapConfigured, — same for repairs — #146
 *   carpetJobCount,              — # of CARPET ActiveQueue docs assigned to
 *                                  this installer that cover `dateString`
 *   usedYds,                     — sum of carpet yardage
 *   usedFurniture,               — sum of furniture pieces (qty across SKU-
 *                                  matched line items)
 *   usedSteps,                   — sum of step pieces (qty across SKU-matched
 *                                  line items)
 *   usedJobStops,                — # of scheduled docs (statusCode 4,
 *                                  classification != "Repair Order") assigned
 *                                  to this installer covering this date — #146
 *   usedRepairStops,             — # of scheduled docs (statusCode 4,
 *                                  classification == "Repair Order") assigned
 *                                  to this installer covering this date — #146
 *   remaining,                   — max(0, effectiveCap - usedYds)
 *   remainingFurniture,          — max(0, effectiveFurnitureCap - usedFurniture)
 *   remainingSteps,              — max(0, effectiveStepsCap - usedSteps)
 *   remainingJobStops,           — max(0, effectiveStopsJobsCap - usedJobStops)
 *                                  when configured; `Infinity` when not — #146
 *   remainingRepairStops,        — same shape for repairs — #146
 *   hasPartialCapacity,          — carpetJobCount >= 1 AND remaining > 0
 *                                  AND installer is Soft Surface AND
 *                                  effectiveCap > 0
 *                                  (yards-only gate — furniture/steps/stops
 *                                  don't factor into the cell-flag trigger;
 *                                  they only filter recommended jobs)
 *   isSoftSurfaceInstaller,      — convenience flag for callers
 * }
 *
 * The shape is intentionally rich so the recommender popover can show "X yds /
 * Y furniture / Z steps remaining" alongside its job list without re-deriving
 * the math.
 */
export function computeRemainingCarpetCapacity({
  candidate,
  dateString,
  activeQueueDocs,
  furnitureSkuList,
  stepsSkuList,
  normalizePayroll,
}) {
  const empty = {
    capacityOnce: 0,
    capacityTwice: 0,
    effectiveCap: 0,
    capacityOnceFurniture: 0,
    capacityTwiceFurniture: 0,
    effectiveFurnitureCap: 0,
    capacityOnceSteps: 0,
    capacityTwiceSteps: 0,
    effectiveStepsCap: 0,
    // #146 — stops dimensions (HARD CAPS on the recommender)
    capacityStopsJobs: 0,
    capacityStopsRepairs: 0,
    effectiveStopsJobsCap: 0,
    effectiveStopsRepairsCap: 0,
    isStopsJobsCapConfigured: false,
    isStopsRepairsCapConfigured: false,
    carpetJobCount: 0,
    usedYds: 0,
    usedFurniture: 0,
    usedSteps: 0,
    usedJobStops: 0,
    usedRepairStops: 0,
    remaining: 0,
    remainingFurniture: 0,
    remainingSteps: 0,
    remainingJobStops: Infinity, // "not configured" default — no constraint
    remainingRepairStops: Infinity,
    hasPartialCapacity: false,
    isSoftSurfaceInstaller: false,
  };

  if (!candidate || !dateString) return empty;
  if (!Array.isArray(activeQueueDocs)) return { ...empty };

  const isSoft = candidate.Scheduling_Surface === "Soft Surface";
  // Raw capacity values from MongoDB are SQFT for carpet yards/twice (per
  // 2026-05-19 UAT review #3); furniture + steps are piece counts. Convert
  // sqft → yards via SQFT_PER_SQ_YD so every comparison + display value in
  // this module operates in yards consistently.
  const capacityOnceSqft = parseFloat(candidate.capacity_carpet_once);
  const capacityTwiceSqft = parseFloat(candidate.capacity_carpet_twice);
  const capacityOnceFurniture = parseFloat(candidate.capacity_carpet_once_furniture);
  const capacityTwiceFurniture = parseFloat(candidate.capacity_carpet_twice_furniture);
  const capacityOnceSteps = parseFloat(candidate.capacity_carpet_once_steps);
  const capacityTwiceSteps = parseFloat(candidate.capacity_carpet_twice_steps);
  const safeOnce =
    (isNaN(capacityOnceSqft) ? 0 : capacityOnceSqft) / SQFT_PER_SQ_YD;
  const safeTwice =
    (isNaN(capacityTwiceSqft) ? 0 : capacityTwiceSqft) / SQFT_PER_SQ_YD;
  const safeOnceFurniture = isNaN(capacityOnceFurniture) ? 0 : capacityOnceFurniture;
  const safeTwiceFurniture = isNaN(capacityTwiceFurniture) ? 0 : capacityTwiceFurniture;
  const safeOnceSteps = isNaN(capacityOnceSteps) ? 0 : capacityOnceSteps;
  const safeTwiceSteps = isNaN(capacityTwiceSteps) ? 0 : capacityTwiceSteps;

  // #146 — stops caps. Treat missing/null/NaN as "not configured" (no cap
  // enforced). Real number (including 0) means the cap is configured and the
  // recommender's stops fit-gate kicks in.
  const rawCapStopsJobs = candidate.capacity_stops_jobs;
  const rawCapStopsRepairs = candidate.capacity_stops_repairs;
  const parsedStopsJobs = parseFloat(rawCapStopsJobs);
  const parsedStopsRepairs = parseFloat(rawCapStopsRepairs);
  const isStopsJobsCapConfigured =
    rawCapStopsJobs !== undefined &&
    rawCapStopsJobs !== null &&
    !isNaN(parsedStopsJobs);
  const isStopsRepairsCapConfigured =
    rawCapStopsRepairs !== undefined &&
    rawCapStopsRepairs !== null &&
    !isNaN(parsedStopsRepairs);
  const safeStopsJobs = isStopsJobsCapConfigured ? parsedStopsJobs : 0;
  const safeStopsRepairs = isStopsRepairsCapConfigured ? parsedStopsRepairs : 0;

  // Short-circuit: installers with no carpet capacity at all return
  // zero-state — `_once === 0 AND _twice === 0` means they're flat-out not
  // qualified to do any carpet work. We still report capacity numbers
  // (useful for diagnostic display) but hasPartialCapacity stays false.
  //
  // 2026-05-20 (#147): the previous version of this gate ALSO required
  // `Scheduling_Surface === "Soft Surface"`. Brandon's testing surfaced
  // installers like Sergio Osorio at ATL who have non-zero carpet capacity
  // but are classified Hard Surface — they were getting silently excluded.
  // Relaxed: `isSoftSurfaceInstaller` is still reported in the return shape
  // for informational/UI use, but it no longer gates the calc.
  if (safeOnce === 0 && safeTwice === 0) {
    return {
      ...empty,
      capacityOnce: safeOnce,
      capacityTwice: safeTwice,
      capacityOnceFurniture: safeOnceFurniture,
      capacityTwiceFurniture: safeTwiceFurniture,
      capacityOnceSteps: safeOnceSteps,
      capacityTwiceSteps: safeTwiceSteps,
      capacityStopsJobs: safeStopsJobs,
      capacityStopsRepairs: safeStopsRepairs,
      isStopsJobsCapConfigured,
      isStopsRepairsCapConfigured,
      isSoftSurfaceInstaller: isSoft,
    };
  }

  const normalize = normalizePayroll || ((v) => v);
  const installerPayroll = normalize(candidate.payrollNo);

  // Filter active-queue docs to: assigned (statusCode 4), carpet surface,
  // assigned to THIS installer, date range covers the target date.
  // We intentionally don't filter by location here — production renders
  // shared-installer jobs from other locations the same way (mirrors the
  // production comment at schedule.context.js:1893).
  let count = 0;
  let usedYds = 0;
  let usedFurniture = 0;
  let usedSteps = 0;
  // #157 (2026-05-22) — track the LARGEST per-job yds/furniture/steps on this
  // date for each dimension. Under Brandon's per-job-cap-with-extras-budget
  // model, the "main" job is the largest one; the rest are extras that share
  // a budget = twice cap. We need max-by-dimension separately because a job
  // could be the largest in yards but smallest in furniture, or vice versa.
  let maxJobYds = 0;
  let maxJobFurniture = 0;
  let maxJobSteps = 0;
  // #146 — stops counters. Job-stops count any-surface scheduled docs
  // (statusCode 4) assigned to this installer on this date with
  // classification != "Repair Order". Repair-stops count the same but with
  // classification == "Repair Order". The classification field on
  // ActiveQueue is the discriminator (see schedule.context.js:283-303 for
  // the canonical filter values).
  let usedJobStops = 0;
  let usedRepairStops = 0;
  for (const doc of activeQueueDocs) {
    if (!doc) continue;
    if (normalize(doc.installer) !== installerPayroll) continue;
    if (!dateInJobRange(dateString, doc.date_scheduled, doc.date_estimatedCompletion)) continue;
    const statusCode = parseFloat(doc.statusCode);
    if (statusCode !== 4) continue;

    // Stops counting is surface-agnostic — any scheduled doc covering this
    // date for this installer consumes 1 stop, regardless of surface.
    if (doc.classification === "Repair Order") {
      usedRepairStops += 1;
    } else {
      usedJobStops += 1;
    }

    // Carpet-specific counters (existing yards/furniture/steps math) still
    // require surface === "Carpet".
    if (doc.surface !== "Carpet") continue;
    count += 1;
    const jobYds = getCarpetYardsForJob(doc);
    const jobFurniture = getCarpetFurniturePiecesForJob(doc, furnitureSkuList);
    const jobSteps = getCarpetStepsForJob(doc, stepsSkuList);
    usedYds += jobYds;
    usedFurniture += jobFurniture;
    usedSteps += jobSteps;
    if (jobYds > maxJobYds) maxJobYds = jobYds;
    if (jobFurniture > maxJobFurniture) maxJobFurniture = jobFurniture;
    if (jobSteps > maxJobSteps) maxJobSteps = jobSteps;
  }

  // #157 (2026-05-22, Brandon + Operations) — per-job cap with extras-budget
  // model. SUPERSEDES the prior daily-budget model.
  //
  // OLD model (daily budget):
  //   effectiveCap = count >= 2 ? safeTwice : safeOnce;
  //   remaining = max(0, effectiveCap - usedYds);
  // OLD model was wrong: at count=1 with usedYds=112, it offered remaining =
  // 180-112 = 68 yds → recommendations had to fit in 68 yds. Brandon's intent
  // was that a 2nd job could be up to safeTwice (150 yds), independent of
  // the 1st job's size.
  //
  // NEW model (per-job cap + extras-budget):
  //   - 1st (MAIN) job is bounded by safeOnce (per-job cap for the largest job
  //     of the day).
  //   - Jobs 2..N (EXTRAS) where N = capacity_stops_jobs: COMBINED yds ≤
  //     safeTwice (shared budget across all extras).
  //   - ELIGIBILITY: extras are allowed only if the main job's yds ≤ safeTwice
  //     (the "one large or two smaller" rule). A main job greater than safeTwice
  //     locks the day as "one large job" mode — no extras.
  //   - Same shape per dimension (yards, furniture, steps) with each dimension's
  //     own once/twice caps + max-job tracking.
  //
  // For Example A (DMS 3163.01, once=180, twice=150, stops=2, 1st=112, candidate=96):
  //   - mainYds = 112  → main job uses 112/180 (within once cap ✓)
  //   - isEligibleForExtras = 112 ≤ 150 ✓ → day is in "smaller jobs" mode
  //   - extrasUsedYds = 0 (no jobs beyond the main yet)
  //   - effectiveCap (next slot) = min(safeTwice, safeTwice - extrasUsedYds)
  //                              = min(150, 150) = 150
  //   - Candidate 96 ≤ 150 ✓ → recommend
  //
  // For a stops=3 case (1st=112, 2nd=30, candidate=80, twice=150):
  //   - mainYds = max(112, 30) = 112; extrasUsedYds = 30
  //   - isEligibleForExtras = 112 ≤ 150 ✓
  //   - extras budget remaining = 150 - 30 = 120
  //   - effectiveCap = min(safeTwice, 120) = 120
  //   - Candidate 80 ≤ 120 ✓ → recommend
  //
  // For "one large" case (1st=170, candidate=anything, twice=150, once=180):
  //   - mainYds = 170; isEligibleForExtras = 170 ≤ 150 = false → no extras
  //   - effectiveCap = 0 → no recommendations
  //
  // Single-load installers (safeTwice === 0): isEligibleForExtras requires
  // mainYds ≤ 0, which is impossible once count ≥ 1 (any scheduled job has
  // positive yds). So the day auto-locks to "one large" after the first job
  // — the same behavior as the prior #144 isFullyBookedByOneLoadCap clamp,
  // unified naturally under the new rule.
  const isEligibleForExtras = count === 0 ? false : maxJobYds <= safeTwice;
  const isEligibleForExtrasFurniture =
    count === 0 ? false : maxJobFurniture <= safeTwiceFurniture;
  const isEligibleForExtrasSteps =
    count === 0 ? false : maxJobSteps <= safeTwiceSteps;
  // "Extras used" = sum of extras = total - main. (count=0 → no main yet,
  // extras = 0; count=1 → main consumes all usedYds, extras = 0; count≥2 →
  // extras = sum of all jobs except the largest.)
  const extrasUsedYds = count === 0 ? 0 : Math.max(0, usedYds - maxJobYds);
  const extrasUsedFurniture =
    count === 0 ? 0 : Math.max(0, usedFurniture - maxJobFurniture);
  const extrasUsedSteps =
    count === 0 ? 0 : Math.max(0, usedSteps - maxJobSteps);
  // Effective cap for the NEXT slot the recommender is filling:
  //   count=0       → candidate becomes the main → bounded by safeOnce
  //   count≥1 OK    → candidate becomes an extra → bounded by min(safeTwice,
  //                   safeTwice - extrasUsed)
  //   count≥1 ineligible → no extras allowed → 0
  const effectiveCap =
    count === 0
      ? safeOnce
      : isEligibleForExtras
      ? Math.min(safeTwice, Math.max(0, safeTwice - extrasUsedYds))
      : 0;
  const effectiveFurnitureCap =
    count === 0
      ? safeOnceFurniture
      : isEligibleForExtrasFurniture
      ? Math.min(
          safeTwiceFurniture,
          Math.max(0, safeTwiceFurniture - extrasUsedFurniture)
        )
      : 0;
  const effectiveStepsCap =
    count === 0
      ? safeOnceSteps
      : isEligibleForExtrasSteps
      ? Math.min(safeTwiceSteps, Math.max(0, safeTwiceSteps - extrasUsedSteps))
      : 0;
  // #146 — stops effective caps. NOT clamped by one-load — repairs must
  // remain recommendable even when jobs side is fully booked. Stops are a
  // single per-installer value with no _once/_twice variant.
  const effectiveStopsJobsCap = safeStopsJobs;
  const effectiveStopsRepairsCap = safeStopsRepairs;
  // #157 — under the new model, "remaining" IS effectiveCap. The popover
  // header shows this as "next job max yds" semantically (no subtraction
  // needed at the caller — the helper already accounts for extras consumed).
  const remaining = effectiveCap;
  const remainingFurniture = effectiveFurnitureCap;
  const remainingSteps = effectiveStepsCap;
  // #146 — remaining stops: when configured, normal max(0, cap - used);
  // when NOT configured, Infinity (no cap enforced). This lets the
  // recommender treat the constraint as a no-op for installers that haven't
  // had their stops caps populated yet (safe transition default).
  const remainingJobStops = isStopsJobsCapConfigured
    ? Math.max(0, effectiveStopsJobsCap - usedJobStops)
    : Infinity;
  const remainingRepairStops = isStopsRepairsCapConfigured
    ? Math.max(0, effectiveStopsRepairsCap - usedRepairStops)
    : Infinity;

  return {
    capacityOnce: safeOnce,
    capacityTwice: safeTwice,
    effectiveCap,
    capacityOnceFurniture: safeOnceFurniture,
    capacityTwiceFurniture: safeTwiceFurniture,
    effectiveFurnitureCap,
    capacityOnceSteps: safeOnceSteps,
    capacityTwiceSteps: safeTwiceSteps,
    effectiveStepsCap,
    // #146 — stops dimensions
    capacityStopsJobs: safeStopsJobs,
    capacityStopsRepairs: safeStopsRepairs,
    effectiveStopsJobsCap,
    effectiveStopsRepairsCap,
    isStopsJobsCapConfigured,
    isStopsRepairsCapConfigured,
    carpetJobCount: count,
    usedYds,
    usedFurniture,
    usedSteps,
    usedJobStops,
    usedRepairStops,
    // #157 — derived state from the per-job + extras-budget model. These are
    // exposed for the popover header and for test transparency.
    maxJobYds,
    maxJobFurniture,
    maxJobSteps,
    extrasUsedYds,
    extrasUsedFurniture,
    extrasUsedSteps,
    isEligibleForExtras,
    isEligibleForExtrasFurniture,
    isEligibleForExtrasSteps,
    remaining,
    remainingFurniture,
    remainingSteps,
    remainingJobStops,
    remainingRepairStops,
    // Cell-flag trigger fires when (a) any work is assigned today AND (b)
    // the installer has remaining capacity in ANY recommendable dimension:
    // carpet yards (Tier 1 + Tier 2 jobs) OR repair-stops (Tier 3 repairs).
    // Furniture/steps are recommender filters, not cell-decision gates.
    //
    // 2026-05-20 (#140 conflict resolution): an installer with jobs side
    // fully booked (yards exhausted or one-load clamp active) but with
    // repair-stops capacity remaining must STILL trigger the red flag so
    // Tier 3 repair recommendations are reachable. Per Leland's spec: "if
    // they've got 12 jobs scheduled for a day, and their yardage is 2 yards
    // for once a day and 2 yards for twice a day, we still want to show
    // recommendations for possible repair orders to also schedule that day."
    //
    // Empty days (no work assigned) are #138's job, not #140's — we still
    // require `usedJobStops + usedRepairStops >= 1` as the "partial-day"
    // precondition.
    //
    // 2026-05-20 (#147): the "any work assigned today" check uses total
    // scheduled-job count across ALL surfaces (`usedJobStops +
    // usedRepairStops`), NOT just carpet jobs. Mixed-surface days (1 vinyl
    // + carpet capacity) must surface red.
    hasPartialCapacity:
      usedJobStops + usedRepairStops >= 1 &&
      ((remaining > 0 && effectiveCap > 0) ||
        // "Not configured" safe transition default: only fire the cell on
        // repair-stops remaining when the cap is EXPLICITLY configured.
        // Otherwise `remainingRepairStops` is `Infinity` and would
        // erroneously fire red on every fully-yards-booked cell whose
        // installer hasn't had repair-stops populated yet.
        (isStopsRepairsCapConfigured && remainingRepairStops > 0)),
    isSoftSurfaceInstaller: isSoft,
  };
}
